import cn from 'classnames'
import { type ReactNode, useCallback, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { ChevronIcon } from './icons'
import { JsonNodeMenu } from './JsonNodeMenu'
import {
  childPath,
  collectMatchPaths,
  copyText,
  getNodeCopyText,
  getPreview,
  isContainer,
  truncate,
} from './lib'
import type { TJsonCopyMode } from './lib'
import { NETWORK_DEBUG_TRANSLATION_KEYS } from './translations'

import styles from './JsonTree.module.scss'

const KEYS = NETWORK_DEBUG_TRANSLATION_KEYS

const PAGE_SIZE = 50
const ROW_LIMIT = 2000
const STRING_LIMIT = 140

/** Object fields read in name order; list items keep theirs. */
const byName = (a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base' })

interface IJsonTreeProps {
  value: unknown
  rootLabel: string
  query?: string
  /** Paths open from the start besides the root, e.g. `response.data`. */
  defaultOpen?: string[]
}

interface INodeMenu {
  node: unknown
  path: string
  label: string
  isIndex: boolean
  x: number
  y: number
}

const highlight = (text: string, query: string): ReactNode => {
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1

  if (index < 0) {
    return text
  }

  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.mark}>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  )
}

const renderPrimitive = (value: unknown, query: string) => {
  if (value === null || value === undefined) {
    return <span className={styles.null}>{String(value)}</span>
  }

  if (typeof value === 'string') {
    return <>&quot;{highlight(truncate(value, STRING_LIMIT), query)}&quot;</>
  }

  return highlight(String(value), query)
}

/** A collapsible view of any JSON value. Long lists open a page at a time; while searching, only
 *  the branches leading to a match are shown, already open. A right click on a row copies it. */
export const JsonTree = ({ value, rootLabel, query = '', defaultOpen = [] }: IJsonTreeProps) => {
  const t = useSafetyTranslation()
  const [menu, setMenu] = useState<INodeMenu | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([rootLabel, ...defaultOpen]))
  const [pages, setPages] = useState<Record<string, number>>({})
  const trimmedQuery = query.trim()

  const matches = useMemo(
    () => (trimmedQuery ? collectMatchPaths(value, rootLabel, trimmedQuery) : null),
    [value, rootLabel, trimmedQuery]
  )

  const toggle = useCallback((path: string) => {
    setExpanded((current) => {
      const next = new Set(current)

      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }

      return next
    })
  }, [])

  const showMore = useCallback((path: string) => {
    setPages((current) => ({ ...current, [path]: (current[path] ?? 1) + 1 }))
  }, [])

  const closeMenu = useCallback(() => setMenu(null), [])

  const copyNode = (mode: TJsonCopyMode) => {
    if (menu) {
      copyText(getNodeCopyText(menu.node, mode, menu.path, menu.label), t(KEYS.copied))
    }

    setMenu(null)
  }

  const rows: ReactNode[] = []

  const renderNode = (
    node: unknown,
    path: string,
    label: string,
    depth: number,
    isIndex: boolean
  ) => {
    if (rows.length >= ROW_LIMIT) {
      return
    }

    const container = isContainer(node)
    const isOpen = container && (matches ? matches.has(path) : expanded.has(path))
    const labelNode = isIndex ? (
      <span className={styles.index}>{label}</span>
    ) : (
      <>
        <span className={styles.key}>{highlight(label, trimmedQuery)}</span>:
      </>
    )

    rows.push(
      <div
        key={path}
        className={cn(styles.row, {
          [styles.container]: container,
          [styles.open]: isOpen,
          [styles.active]: menu?.path === path,
        })}
        style={{ paddingLeft: depth * 16 + 4 }}
        onClick={container ? () => toggle(path) : undefined}
        onContextMenu={(event) => {
          event.preventDefault()
          setMenu({ node, path, label, isIndex, x: event.clientX, y: event.clientY })
        }}
      >
        <span className={styles.twisty}>
          {container && <ChevronIcon className={styles.chevron} />}
        </span>
        {labelNode}{' '}
        {container ? (
          <>
            <span className={styles.count}>
              {Array.isArray(node) ? `[${node.length}]` : `{${Object.keys(node).length}}`}
            </span>
            {!isOpen && <span className={styles.preview}>{getPreview(node)}</span>}
          </>
        ) : (
          <span className={styles.value}>{renderPrimitive(node, trimmedQuery)}</span>
        )}
      </div>
    )

    if (!isOpen) {
      return
    }

    const isArray = Array.isArray(node)
    const keys = isArray ? node.map((_, index) => String(index)) : Object.keys(node).sort(byName)
    const visibleKeys = matches
      ? keys.filter((key) => matches.has(childPath(path, key, isArray)))
      : keys
    const limit = (pages[path] ?? 1) * PAGE_SIZE

    visibleKeys.slice(0, limit).forEach((key) => {
      const child = isArray
        ? (node as unknown[])[Number(key)]
        : (node as Record<string, unknown>)[key]
      renderNode(child, childPath(path, key, isArray), key, depth + 1, isArray)
    })

    if (visibleKeys.length > limit) {
      const left = visibleKeys.length - limit

      rows.push(
        <div
          key={`${path}:more`}
          className={styles.row}
          style={{ paddingLeft: (depth + 1) * 16 + 4 }}
        >
          <span className={styles.twisty} />
          <button type="button" className={styles.more} onClick={() => showMore(path)}>
            <Translation
              text={KEYS.showMore}
              span
              replaceParams={[
                { key: '{count}', value: String(Math.min(PAGE_SIZE, left)) },
                { key: '{left}', value: left.toLocaleString('en-US') },
              ]}
            />
          </button>
        </div>
      )
    }
  }

  renderNode(value, rootLabel, rootLabel, 0, false)

  if (matches && matches.size === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyTitle}>
          <Translation
            text={KEYS.noMatches}
            span
            replaceParams={[{ key: '{query}', value: trimmedQuery }]}
          />
        </span>
        <Translation text={KEYS.noMatchesText} span />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {rows}
      {menu && (
        <JsonNodeMenu
          x={menu.x}
          y={menu.y}
          hasKey={!menu.isIndex}
          onCopy={copyNode}
          onClose={closeMenu}
        />
      )}
    </div>
  )
}

JsonTree.displayName = 'JsonTree'
